import { useCallback, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  LoadingIndicator,
  Modal,
  Typography,
  Banner,
  BannerTypes,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import {
  HeaderButton,
  HeaderContentLayout,
  // eslint-disable-next-line import/no-restricted-paths
} from "../templates/HeaderContentLayout";
import { postAttachment } from "services/prospect";
import { useAppSelector } from "store";
import { Attachment } from "utils/usageUtils";
import { useUsageData } from "utils/swrHooks";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { ErrorModal } from "components/molecules/ErrorModal";
import { DocumentUpload } from "components/organisms/DocumentUpload";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";
import { UsageFormContext } from "providers/UsageFormContext";

const UtilityBillSlideout = ({
  onClose,
  onSave,
  onGetBillOnline,
  intervalDataInitialLoading,
  intervalDataStatus,
}: {
  onClose: () => void;
  onSave?: () => void;
  onGetBillOnline?: () => void;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
}) => {
  const usageContext = useContext(UsageFormContext);
  const { prospectId } = useParams();
  const authKey = useAppSelector((state) => state?.auth?.hybridToken);

  const [newAttachments, setNewAttachments] = useState<Attachment[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [showExitModal, setShowExitModal] = useState(false);

  const {
    attachments,
    error: usageError,
    isLoading: isUsageLoading,
    refetch: usageRefetch,
  } = useUsageData(prospectId);

  const handleSave = useCallback(() => {
    if (!prospectId) return;

    setIsSaving(true);
    const uploadPromises = newAttachments.map((attachment: Attachment) =>
      postAttachment(authKey, prospectId, attachment)
    );

    Promise.all(uploadPromises)
      .then(async () => {
        await usageRefetch();
        setIsSaving(false);
        onSave?.();
        onClose();
      })
      .catch((err) => {
        setIsSaving(false);
        setSaveError(err);
      });
  }, [prospectId, authKey, newAttachments, usageRefetch, onSave, onClose]);

  const handleCancel = useCallback(() => {
    if (usageContext.isDirty) {
      setShowExitModal(true);
    } else {
      onClose();
    }
  }, [usageContext.isDirty, onClose]);

  const buttons = useMemo(
    () => [
      {
        type: "secondary",
        text: "Cancel & Close",
        onClick: handleCancel,
      } as HeaderButton,
      {
        type: "primary",
        text: "Save",
        onClick: handleSave,
      } as HeaderButton,
    ],
    [handleCancel, handleSave]
  );

  if (usageError) {
    return (
      <Slideout>
        <HeaderContentLayout
          content={
            <ErrorComponent
              context="UtilityBillSlideout"
              action="Get Usage"
              error={usageError}
            />
          }
          title="There was a Problem."
          description="Something went wrong. Please try refreshing the page."
        />
      </Slideout>
    );
  }

  if (isUsageLoading || isSaving) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose} data-testid="utility-bill-slideout">
      {!!saveError && (
        <ErrorModal
          context="UtilityBillSlideout"
          error={saveError}
          action="Saving Utility Bill"
          onClose={() => setSaveError(null)}
        />
      )}
      {showExitModal && usageContext.isDirty && (
        <Modal
          testId="utility-bill-slideout-exit-modal"
          type="warning"
          title="Are you sure you want to exit without saving?"
          onClose={() => setShowExitModal(false)}
          primaryButton={{
            text: "Save & Close",
            onClick: handleSave,
          }}
          secondaryButton={{
            text: "Close",
            onClick: onClose,
          }}
        >
          <Typography variant="p">
            All of your unsaved changes will be discarded.
          </Typography>
        </Modal>
      )}
      <HeaderContentLayout
        title="Usage & Utility"
        buttons={buttons}
        content={
          <StyledUtilityBillSlideoutContent>
            <Typography variant="p">
              To find your best system, we need to understand your current
              electricity rate.
            </Typography>
            <Banner
              bannerType={BannerTypes.InfoLight}
              show={true}
              showClose={false}
              primaryAction={{
                title: "Get Bill Online",
                action: () => {
                  onGetBillOnline?.();
                  onClose();
                },
                testId: "get-bill-online-button",
              }}
            >
              <Typography variant="p">
                Use our simple online tool to get your utility bill
                automatically.
              </Typography>
            </Banner>
            <DocumentUpload
              uploadedFiles={attachments ?? []}
              uploadedNewFiles={newAttachments}
              setUploadedNewFiles={setNewAttachments}
              documentType="Utility Bill"
              description="Upload monthly utility bill(s) for review."
              title="Utility Bills"
              intervalDataInitialLoading={intervalDataInitialLoading}
              intervalDataStatus={intervalDataStatus}
            />
          </StyledUtilityBillSlideoutContent>
        }
      />
    </Slideout>
  );
};

const StyledUtilityBillSlideoutContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export { UtilityBillSlideout };
